import { Typography, Tag } from 'antd';
import styled from '@emotion/styled';
import { motion, AnimatePresence, useIsPresent } from 'motion/react';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { updateWorkflowCreatePayload } from '../../../../store/workflows/slice';

const { Text } = Typography;

const FIELD_OPTIONS = [
  { label: 'IMAGES', value: 'IMAGES' },
  { label: 'PRODUCT_NAME', value: 'PRODUCT_NAME' },
  { label: 'BRAND', value: 'BRAND' },
  { label: 'COLOR', value: 'COLOR' },
  { label: 'FABRIC', value: 'FABRIC' },
];

const ALWAYS_PRESENT: Array<string> = ['IMAGES'];

const StyledContainer = styled.div`
  padding: 20px;
`;

const OptionsSection = styled.div`
  margin-top: 12px;
  margin-bottom: 16px;
  padding-left: 12px;
`;

const SelectedSection = styled.div`
  margin-top: 16px;
  padding-left: 12px;
`;

const SectionLabel = styled(Text)`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 12px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SelectableTag = styled(Tag)<{ selected: boolean }>`
  cursor: pointer;
  user-select: none;
  border: 1px solid ${props => (props.selected ? '#1890ff' : '#d9d9d9')};
  background: ${props => (props.selected ? '#e6f7ff' : '#ffffff')};
  color: ${props => (props.selected ? '#1890ff' : '#171717')};

  border-radius: 20px;
  padding: 4px 12px;
  margin: 0;
  font-size: 10px;
`;

interface SelectedTagProps {
  field: string;
  onRemove: (field: string) => void;
}

const SelectedTag = ({ field, onRemove }: SelectedTagProps) => {
  const isPresent = useIsPresent();
  const isAlways = ALWAYS_PRESENT.includes(field);

  const animations = {
    style: {
      position: isPresent ? ('static' as const) : ('absolute' as const),
    },
    initial: { scale: 0, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0, opacity: 0 },
    transition: { type: 'spring' as const, stiffness: 400, damping: 25 },
  };

  return (
    <motion.div {...animations} layout>
      <Tag
        closable={!isAlways}
        onClose={() => onRemove(field)}
        style={{
          background: '#ffffff',
          border: '1px solid #E9ECEF',
          color: '#171717',
          borderRadius: 20,
          padding: '4px 12px',
          margin: 0,
          fontSize: '10px',
        }}
      >
        {field}
      </Tag>
    </motion.div>
  );
};

const Fields = () => {
  const dispatch = useAppDispatch();

  const fields = useAppSelector(
    state => state.workflows.workflowCreatePayload?.fields ?? [...ALWAYS_PRESENT],
  );

  const handleOptionClick = (optionValue: string) => {
    if (fields.includes(optionValue)) {
      if (ALWAYS_PRESENT.includes(optionValue)) return;

      const newFields = fields.filter(f => f !== optionValue);
      dispatch(updateWorkflowCreatePayload({ fields: newFields }));
    } else {
      const newFields = [...fields, optionValue];
      dispatch(updateWorkflowCreatePayload({ fields: newFields }));
    }
  };

  const handleRemoveField = (fieldValue: string) => {
    if (ALWAYS_PRESENT.includes(fieldValue)) return;

    const newFields = fields.filter(f => f !== fieldValue);
    dispatch(updateWorkflowCreatePayload({ fields: newFields }));
  };

  return (
    <StyledContainer>
      <Text>Fields to pass for tagging</Text>

      <OptionsSection>
        <SectionLabel>Available Options:</SectionLabel>

        <TagsContainer>
          {FIELD_OPTIONS.map(option => (
            <motion.div
              key={option.value}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <SelectableTag
                selected={fields.includes(option.value)}
                onClick={() => handleOptionClick(option.value)}
              >
                {option.label}
              </SelectableTag>
            </motion.div>
          ))}
        </TagsContainer>
      </OptionsSection>

      <SelectedSection>
        <SectionLabel>Selected Fields:</SectionLabel>
        <TagsContainer>
          <AnimatePresence>
            {fields.map(field => (
              <SelectedTag key={field} field={field} onRemove={handleRemoveField} />
            ))}
          </AnimatePresence>
        </TagsContainer>
      </SelectedSection>
    </StyledContainer>
  );
};

export default Fields;
